import { useState } from 'react';
import { Check } from 'lucide-react';
import { clsx } from '../../../ds/clsx';
import { Eyebrow } from '../../../ds/components';
import type { ChecklistCategory, ChecklistItem, WizardDocument } from '../types';
import { DeveloperLinkCard } from './DeveloperLinkCard';
import type { useChecklistActions } from './useChecklistActions';
import styles from './wizard.module.css';

const VISIBLE_DONE = 5;

type Actions = ReturnType<typeof useChecklistActions>;

function ProgressBar({
  label,
  done,
  total,
  accent,
}: {
  label: string;
  done: number;
  total: number;
  accent?: boolean;
}) {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <div>
      <div className={styles.progressLabelRow}>
        <span className={styles.progressLabel}>{label}</span>
        <span className={styles.progressValue}>
          {done} von {total}
        </span>
      </div>
      <div
        className={styles.progressBar}
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
      >
        <span
          className={clsx(styles.progressBarFill, accent && styles.progressBarFillAccent)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

interface ItemRowProps {
  item: ChecklistItem;
  canEdit: boolean;
  actions: Actions;
}

function ItemRow({ item, canEdit, actions }: ItemRowProps) {
  const busy = actions.busyId === item.id;
  const target = { id: item.id, title: item.title, documentTypeId: item.documentTypeId };
  const openRequired = !item.done && item.required;
  return (
    <div className={styles.item}>
      <button
        type="button"
        role="checkbox"
        aria-checked={item.done}
        aria-label={item.title}
        className={clsx(styles.itemCheck, item.done && styles.itemCheckDone)}
        disabled={!canEdit || busy}
        aria-busy={busy || undefined}
        onClick={() => void actions.toggle(target, !item.done)}
      >
        {item.done ? <Check size={11} strokeWidth={2.5} aria-hidden="true" /> : null}
      </button>
      <span className={styles.itemText}>
        <span className={styles.itemTitle}>
          {item.title}
          {item.required ? <span className={styles.itemRequired}>Pflicht</span> : null}
        </span>
        {item.subtext ? (
          <span className={clsx(styles.itemSub, openRequired && styles.itemSubWarn)}>{item.subtext}</span>
        ) : null}
      </span>
      {!item.done && canEdit && (item.documentTypeId || item.action) ? (
        <span className={styles.itemActions}>
          {item.documentTypeId ? (
            <button
              type="button"
              className={clsx('pressable', styles.smallButton)}
              disabled={actions.busyId !== null}
              onClick={() => actions.startUpload(target)}
            >
              Hochladen
            </button>
          ) : null}
          {item.action === 'request' ? (
            <button
              type="button"
              className={clsx('pressable', styles.smallButton)}
              disabled={actions.busyId !== null}
              aria-busy={busy || undefined}
              onClick={() => void actions.request(target)}
            >
              Anfordern
            </button>
          ) : item.action === 'remind' ? (
            <button
              type="button"
              className={clsx('pressable', styles.smallButton)}
              disabled={actions.busyId !== null}
              aria-busy={busy || undefined}
              onClick={() => void actions.remind(target)}
            >
              Erinnern
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

interface CategoryProps {
  category: ChecklistCategory;
  open: boolean;
  onToggle: () => void;
  onlyOpen: boolean;
  canEdit: boolean;
  actions: Actions;
}

function Category({ category, open, onToggle, onlyOpen, canEdit, actions }: CategoryProps) {
  const [expanded, setExpanded] = useState(false);
  const panelId = `checklist-${category.key}`;
  const items = onlyOpen ? category.items.filter((item) => !item.done) : category.items;
  let doneShown = 0;
  const visible: ChecklistItem[] = [];
  let hiddenDone = 0;
  for (const item of items) {
    if (item.done && !expanded) {
      if (doneShown >= VISIBLE_DONE) {
        hiddenDone += 1;
        continue;
      }
      doneShown += 1;
    }
    visible.push(item);
  }
  return (
    <div className={styles.category}>
      <button
        type="button"
        className={clsx(styles.categoryHead, open && styles.categoryHeadOpen)}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className={styles.categoryCaret} aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        <span className={styles.categoryTitle}>{category.label}</span>
        <span className={styles.categoryCount}>
          {category.done} von {category.total}
        </span>
      </button>
      {open ? (
        <div id={panelId}>
          {visible.length === 0 ? (
            <div className={styles.itemMore}>{onlyOpen ? 'Keine offenen Punkte.' : 'Keine Punkte.'}</div>
          ) : (
            visible.map((item) => <ItemRow key={item.id} item={item} canEdit={canEdit} actions={actions} />)
          )}
          {hiddenDone > 0 ? (
            <button
              type="button"
              className={clsx(styles.itemMore, styles.linkButton)}
              onClick={() => setExpanded(true)}
            >
              {hiddenDone} weitere erledigt
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function defaultOpenCategory(categories: ChecklistCategory[]): string | null {
  const withOpenRequired = categories.find((category) =>
    category.items.some((item) => !item.done && item.required),
  );
  return (withOpenRequired ?? categories[0])?.key ?? null;
}

export interface ChecklistMainProps {
  doc: WizardDocument;
  actions: Actions;
}

/** Objekt anlegen 06: the checklist card with the two progress bars and the category accordions. */
export function ChecklistMain({ doc, actions }: ChecklistMainProps) {
  const { checklist } = doc;
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(() => defaultOpenCategory(checklist.categories));
  const total = checklist.progress.all.total;
  return (
    <section className={styles.card} aria-labelledby="wizard-checklist">
      <div className={styles.cardHead}>
        <div className={styles.cardHeadText}>
          <h2 id="wizard-checklist" className={styles.cardTitle}>
            Checkliste
          </h2>
          <p className={styles.cardText}>
            {checklist.template
              ? `Vorlage „${checklist.template.name}“ · ${total} Punkte. `
              : `${total} Punkte. `}
            Punkte mit passender Unterlage wurden automatisch abgehakt.
          </p>
        </div>
        <button
          type="button"
          className={styles.linkButton}
          aria-pressed={onlyOpen}
          onClick={() => setOnlyOpen((value) => !value)}
        >
          {onlyOpen ? 'Alle zeigen' : 'Nur offene zeigen'}
        </button>
      </div>
      <div className={styles.progressPair}>
        <ProgressBar
          label="Alle Punkte"
          done={checklist.progress.all.done}
          total={checklist.progress.all.total}
        />
        <ProgressBar
          label="Pflichtpunkte"
          done={checklist.progress.required.done}
          total={checklist.progress.required.total}
          accent
        />
      </div>
      <div className={styles.categories}>
        {checklist.categories.map((category) => (
          <Category
            key={category.key}
            category={category}
            open={openKey === category.key}
            onToggle={() => setOpenKey((current) => (current === category.key ? null : category.key))}
            onlyOpen={onlyOpen}
            canEdit={checklist.canEdit}
            actions={actions}
          />
        ))}
      </div>
      <input
        ref={actions.inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.dwg,application/pdf,image/jpeg,image/png"
        hidden
        aria-hidden="true"
        tabIndex={-1}
        onChange={actions.onFileChange}
      />
    </section>
  );
}

export interface ChecklistSidebarProps {
  doc: WizardDocument;
  onChanged: () => void;
}

/** Objekt anlegen 06 sidebar: „Vertriebsfreigabe" summary + the developer link card. */
export function ChecklistSidebar({ doc, onChanged }: ChecklistSidebarProps) {
  const open = doc.checklist.openRequired.length;
  return (
    <>
      <section className={styles.card} aria-labelledby="wizard-release-summary">
        <Eyebrow id="wizard-release-summary">Vertriebsfreigabe</Eyebrow>
        <div className={styles.sideText}>
          {open === 0
            ? 'Alle Pflichtpunkte erfüllt.'
            : open === 1
              ? 'Noch 1 Pflichtpunkt offen.'
              : `Noch ${open} Pflichtpunkte offen.`}
        </div>
        <div className={styles.sideTextMuted}>
          Kann-Punkte blockieren die Freigabe nicht und bleiben in der Objektakte sichtbar.
        </div>
      </section>
      <DeveloperLinkCard
        propertyId={doc.property.id}
        link={doc.developerLink}
        variant="checklist"
        onChanged={onChanged}
      />
    </>
  );
}
